<template>
  <v-container fluid>
    <v-card raised elevation="5" style="width: 100%; height: calc(100vh - 22vh);">
      <v-card-text style="height: 100%; padding: 0;">
        <v-row v-resize="onResize" style="height: 100%; margin: 0;">
          <ejs-splitter
            id="splitter"
            ref="splitterObj"
            :separatorSize="3"
            class="splitterContent"
            width="100%"
            height="100%"
          >
            <e-panes>
              <e-pane
                size="30%"
                :collapsible="false"
                :collapsed="false"
                content="#config-list-content"
                min="20%"
              ></e-pane>
              <e-pane
                size="70%"
                min="50%"
                content="#configuration-content"
              ></e-pane>
            </e-panes>
          </ejs-splitter>
          
          <v-card 
            flat 
            id="config-list-content" 
            style="text-align: left; display: flex; flex-direction: column; height: 100%;"
          >
            <v-toolbar color="neutralGray" dark height="56px" flat tile>
              <v-icon dark>mdi-cog-outline</v-icon>
              <span style="margin-left: 5px" class="text-h6">{{
                $t("menu.settings")
              }}</span>
            </v-toolbar>
            <v-list 
              class="pt-0 flex-grow-1" 
              style="height: 0; overflow-y: auto;"
            >
              <v-list-item-group :value="selectedItem" mandatory>
                <template v-for="(settings, settingsName) in configurations">
                  <div class="config-title">
                    <span>{{ $t(settingsName) }}</span>
                  </div>
                  <template v-for="(settingItem, index) in settings">
                    <v-list-item
                      :key="`configuration-list-${settingItem.label}`"
                      @click="setConfig(settingItem)"
                      :value="settingItem.configname"
                      :active-class="
                        selectedItem === settingItem.configname
                          ? 'item-active-class'
                          : ''
                      "
                    >
                      <v-list-item-content>
                        <v-list-item-title class="config-sub-item-title">{{
                          getLabel(settingItem.label)
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider
                      :key="`divider-inner-${settingsName}-${index}`"
                    ></v-divider>
                  </template>
                  <v-divider
                    :key="`divider-outer-${settingsName}`"
                  ></v-divider>
                </template>
              </v-list-item-group>
            </v-list>
          </v-card>
          
          <v-card 
            id="configuration-content" 
            style="text-align: left; display: flex; flex-direction: column; height: 100%;" 
            flat
          >
            <v-toolbar color="secondary" height="56px" flat tile>
              <v-icon>mdi-cog-outline</v-icon>
              <span style="margin-left: 5px" class="text-h6" v-if="setting"
                >{{ getLabel(setting.label) }} -
                {{ $t("admin.lblConfiguration") }}
              </span>
            </v-toolbar>
            <div 
              class="flex-grow-1" 
              style="overflow-y: auto;"
            >
              <transition name="fade" mode="out-in" appear>
                <component
                  v-if="setting"
                  :ref="`ref_${dynamicTab}`"
                  :is="dynamicTab"
                  :setting="setting"
                  :screenHeight="screenHeight"
                  :is-change-prevent.sync="isDirty"
                  :is-open-prevent.sync="isPrevent"
                  @switchConfig="switchConfig"
                  :key="componentKey"
                  @interface="getChildInterface"
                  @discard="onDiscard"
                ></component>
              </transition>
              <v-divider></v-divider>
            </div>
          </v-card>
        </v-row>
      </v-card-text>
    </v-card>
    <confirm-dialog ref="confirm" />
    <prevent-confirmation
      :prevent="isPrevent"
      @save="saveDetails"
      @discard="preventClose(true)"
      @cancel="preventClose(false)"
    >
    </prevent-confirmation>
    <prevent-navigation
      :prevent="isPreventNavigation"
      @save="saveBeforeNavigation($event)"
      @discard="$event.next()"
      @cancel="onNavigationCancel"
    >
    </prevent-navigation>
    <message-sheet
      :showMessage="displayMessage"
      :message="message"
      :messageType="messageType"
      @close="displayMessage = false"
    ></message-sheet>
  </v-container>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
import ConfirmDialog from "../ConfirmDialog.vue";
import Loader from "@/components/Loader";
import MessageSheet from "../MessageSheet.vue";
import SignalRMixin from "../../mixin/SignalRMixin";
import _ from "lodash";
import { L10n } from "@syncfusion/ej2-base";
import AList from "@/wrapper-component/AList.vue";

Vue.prototype.$eventHub = new Vue();
import {
  Edit,
  Toolbar,
  ColumnChooser,
  Resize,
  CommandColumn,
  Page,
} from "@syncfusion/ej2-vue-grids";
import PreventConfirmation from "../PreventConfirmation.vue";
import PreventNavigation from "../PreventNavigation.vue";
import settingGrid from "./settingGrid.vue";
import AgendaNumbering from "./AgendaNumbering.vue";
import ESignatureProvider from "./ESignatureProvider.vue";
import ChunkSetting from "./ChunkSetting.vue";
import DefaultSortOrder from "./DefaultSortOrder.vue";
import DefaultFolding from "./DefaultFolding.vue";
import ActionNotification from "./ActionNotification.vue";
import AnonymityFeedback from "./AnonymityFeedback.vue";
import EmailNotificationTemplate from "./EmailNotificationTemplate.vue";
import DefaultLineNum from "./DefaultLineNum.vue";

export default {
  components: {
    ConfirmDialog,
    Loader,
    MessageSheet,
    AList,
    PreventConfirmation,
    PreventNavigation,
    settingGrid,
    AgendaNumbering,
    ChunkSetting,
    ESignatureProvider,
    DefaultSortOrder,
    DefaultLineNum,
    DefaultFolding,
    AnonymityFeedback,
    EmailNotificationTemplate,
    ActionNotification
  },
  mixins: [SignalRMixin],
  name: "List",
  props: {
    isChangePrevent: {
      type: Boolean,
      default: false,
    },
    isOpenPrevent: {
      type: Boolean,
      default: false,
    },
  },
  provide() {
    return {
      confirmDeleteRef: this.confirmDeleteRef,
      grid: [Edit, Toolbar, ColumnChooser, Resize, CommandColumn, Page],
    };
  },
  data() {
    return {
      routeNextObj: null,
      navigateConfigSetting: null,
      isDirty: false,
      isPrevent: false,
      isPreventNavigation: false,
      displayMessage: false,
      screenHeight: 0,
      messageType: "",
      message: "",
      showAddPeopleDialog: false,
      search: "",
      Loaderloading: false,
      setting: null,
      data: [],
      rowUpdatedData: {},
      selectedItem: 0,
      randomNumber: Math.floor(Math.random() * 1000 + 1),
    };
  },

  computed: {
    ...mapGetters({
      tenantSettingConfig: "tenant/tenantSettingConfig",
      tenantSettingTypes: "tenant/tenantSettingTypes",
      masterSettingTypes: "tenant/masterSettingTypes",
    }),
    LoaderText() {
      return this.$t("LoaderText");
    },
    tenantId() {
      return sessionStorage.getItem("tenantId");
    },
    confirmDeleteRef() {
      return this.$refs;
    },
    settingGrid() {
      if (this.$refs.settingGridRef) {
        return this.$refs.settingGridRef;
      } else {
        return null;
      }
    },
    componentKey: {
      get: function () {
        return this.randomNumber;
      },
      set: function (newValue) {
        this.randomNumber = newValue;
      },
    },
    meetingTypeStorageKey() {
      return "meetingType#" + this.tenantId;
    },
    meetingType() {
      return localStorage.getItem(this.meetingTypeStorageKey);
    },
    configurations() {
      const config = this.masterSettingTypes("settings");

      let groupedByScreenName = config.reduce((acc, curr) => {
        if (!acc[curr.settingCategoryName]) {
          acc[curr.settingCategoryName] = [];
        }
        acc[curr.settingCategoryName].push(curr);
        return acc;
      }, {});

      Object.keys(groupedByScreenName).forEach(key => {
        groupedByScreenName[key].sort((a, b) => a.order - b.order);
      });

      const sortedGroupedByScreenName = {};
      Object.keys(groupedByScreenName)
        .sort((a, b) => {
          const minOrderA = Math.min(...groupedByScreenName[a].map(item => item.order));
          const minOrderB = Math.min(...groupedByScreenName[b].map(item => item.order));
          return minOrderA - minOrderB;
        })
        .forEach(key => {
          sortedGroupedByScreenName[key] = groupedByScreenName[key];
        });
      
      return sortedGroupedByScreenName;
    },
    dynamicTab() {
      const tabMapping = {
        agendanumbering: "AgendaNumbering",
        chunkSetting: "ChunkSetting",
        defaultSortOrder: "DefaultSortOrder",
        defaultLineNum: "DefaultLineNum",
        e_signature: "ESignatureProvider",
        anonymity_feedback: "AnonymityFeedback",
        emailNotificationTemplate: "EmailNotificationTemplate",
        defaultFolding: "DefaultFolding",
        actionNotification: "ActionNotification"
      };

      return this.setting && this.setting.configname
        ? tabMapping[this.setting.configname] || "settingGrid"
        : "settingGrid";     
    },
  },
  watch: {
    isOpenPrevent(value) {
      if (value) {
        this.isPrevent = value;
      }
    },
    isChangePrevent(value) {
      if (value) {
        this.isDirty = value;
      }
    },
    isDirty(value) {
      this.isPreventNavigation = value;
      this.$emit("update:isChangePrevent", value);
    },
    selectedItem(newVal) {
      const configList = this.configurations?.settingTitleMeeting || [];
      const selectedConfig = configList.find(c => c.settingCategoryName === newVal);
      if (selectedConfig) {
        this.setConfig(selectedConfig);
      }
    }
  },
  async created() {
    try {
      this.Loaderloading = true;
      await this.getTenantConfigList();
      await this.getMasterConfigList();
      this.Loaderloading = false;
    } catch (error) {
      this.Loaderloading = false;
    }
  },
  async mounted() {
    this.$eventHub.$on("onMessageDisplay", (params) => {
      this.showMessageSheet(params.message, params.messageType);
    });
    if (this.configurations?.settingTitleMeeting?.length > 0) {
      const firstConfig = this.configurations.settingTitleMeeting[0];
      this.setConfig(firstConfig);
      this.selectedItem = firstConfig.settingCategoryName;
    }
  },
  beforeDestroy() {
    this.$eventHub.$off("onMessageDisplay");
  },
  methods: {
    ...mapActions({
      getTenantConfigList: "tenant/getTenantConfigList",
      getMasterConfigList: "tenant/getMasterConfigList",
      setMeetingType: "meeting/setMeetingType",
    }),
    ...mapMutations({
      setSwitchTenantId: "setSwitchTenantId",
    }),
    getLabel(item) {
      return this.$t(`admin.${item}`);
    },
    // Setting the interface when emitted from child
    getChildInterface(childInterface) {
      this.$options.childInterface = childInterface;
    },
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    async setConfig(config) {
      this.routeNextObj = null;

      const resetState = () => {
        this.setting = null;
        this.forceRerender();
        this.setting = config;
        this.selectedItem = config.configname;
        this.navigateConfigSetting = null;
      };

      const handleDirtyState = () => {
        const oldName = this.selectedItem;
        this.selectedItem = config.configname;
        this.$nextTick(() => {
          this.selectedItem = oldName;
        });
        this.isPrevent = true;
        this.$emit("update:isChangePrevent", true);
        this.navigateConfigSetting = config;
      };

      if (!this.isDirty) {
        resetState();
      } else {
        handleDirtyState();
      }
    },
    forceRerender() {
      this.componentKey = Math.floor(Math.random() * 100 + 1);
    },
    onDiscard() {
      this.$emit("discard");
    },
    onResize,
    saveDetails,
    preventClose,
    switchConfig,
    saveBeforeNavigation,
    onNavigationCancel,
    removeObjects,
  },
};

function removeObjects(array, configname, labelToRemove) {
  return array.map((item) => {
    if (item.configname === configname) {
      item.columns = item.columns.filter(
        (column) => column.label !== labelToRemove
      );
    }
    return item;
  });
}

function onNavigationCancel() {
  const newMeetingType = localStorage.getItem(this.meetingTypeStorageKey);
  if (this.meetingType !== newMeetingType) {
    localStorage.setItem(this.meetingTypeStorageKey, this.meetingType);
    this.setMeetingType(this.meetingType);
  }
}
function saveBeforeNavigation($event) {
  if (this.setting.configname === "defaultSortOrder") {
    this.routeNextObj = $event;
    this.$refs.ref_DefaultSortOrder.saveOnNavigation();
    this.onNavigationCancel();
    this.setSwitchTenantId(sessionStorage.getItem("tenantId"));
    this.isPrevent = false;
  } else {
    this.routeNextObj = $event;
    this.$options.childInterface.endEdit();
    this.onNavigationCancel();
    this.setSwitchTenantId(sessionStorage.getItem("tenantId"));
    this.isPrevent = false;
  }
}

async function saveDetails() {
  const resetState = () => {
    this.routeNextObj = null;
    this.isPrevent = false;
    if (!this.navigateConfigSetting) {
      this.$emit("save");
    }
  };

  if (this.setting.configname === "defaultSortOrder") {
    this.$refs.ref_DefaultSortOrder.saveOnNavigation();
  } else {
    this.$options.childInterface.saveConfig();
  }

  resetState();
}

function switchConfig() {
  if (this.routeNextObj) {
    this.routeNextObj.next();
    this.routeNextObj = null;
  }
  if (this.navigateConfigSetting) {
    this.forceRerender();
    this.setConfig(this.navigateConfigSetting);
    this.navigateConfigSetting = null;
    this.isDirty = false;
    this.isPrevent = false;
  }
  this.isPreventNavigation = false;
  this.$emit("update:isChangePrevent", false);
}

async function preventClose(status) {
  this.isPrevent = false;

  const resetState = () => {
    this.isDirty = false;
    this.isPreventNavigation = false;
    this.$emit("update:isChangePrevent", false);
  };

  const handleDiscard = () => {
    if (this.isOpenPrevent) {
      this.$emit("discard");
      if (this.$options.childInterface?.closeEdit) {
        this.$options.childInterface.closeEdit();
      }
    }
  };

  const handleNavigation = () => {
    this.forceRerender();
    this.setConfig(this.navigateConfigSetting);
    this.navigateConfigSetting = null;
  };

  if (status) {
    resetState();
    handleDiscard();
    if (!this.isOpenPrevent) {
      handleNavigation();
    }
  } else {
    this.$emit("cancel");
    this.navigateConfigSetting = null;
  }
}

function onResize() {
  this.screenHeight = window.innerHeight;
}
</script>

<style scoped>
.item-active-class {
  background: #006cad !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}
::v-deep .e-grid .e-tooltip-wrap.e-griderror {
  z-index: 10 !important;
}
::v-deep .e-grid .e-gridheader {
  background-color: var(--v-appBar-darken1);
  border-bottom-color: #e0e0e0;
  border-top-color: #e0e0e0;
  color: rgba(0, 0, 0, 0.54);
}
::v-deep .e-grid .e-rowcell {
  padding-right: 8px !important;
}
::v-deep .e-grid .e-headercell,
.e-grid .e-detailheadercell {
  font-family: "Noto Sans JP" !important;
  font-style: normal !important;
  background: var(--v-neutralGray-base) !important;
  color: #ffffff !important;
  height: 32px !important;
  min-height: 32px !important;
}

::v-deep .e-grid .e-headercelldiv {
  font-weight: bold;
  font-size: 12px;
}
::v-deep .e-toolbar.e-control {
  box-sizing: content-box;
  background-color: var(--v-appBar-base) !important;
  border-color: var(--v-pageHeader-base) !important;
  color: #ffffff;
  display: grid !important;
}

::v-deep .v-card__text {
  width: 100%;
  padding: 12px !important;
}

::v-deep .e-grid .e-toolbar-items {
  /* background-color: inherit; */
  height: 48px !important;
  min-height: 48px !important;
}
.e-grid .e-toolbar-items {
  background-color: #fff;
}

::v-deep .e-toolbar .e-toolbar-items .e-toolbar-item {
  align-content: center;
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  min-height: 40px !important;
  vertical-align: middle;
  width: auto;
}
.v-toolbar{
  flex: 0 0 auto !important;
}
::v-deep .e-toolbar .e-tbar-btn.e-btn {
  font-family: "Noto Sans JP" !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  background-color: var(--v-primaryDark-base) !important;
  border-color: var(--v-primaryDark-base) !important;
  min-width: 64px !important;
  padding: 0 16px 0 10px !important;
  color: #fff !important;
  align-items: center;
  border-radius: 4px;
  display: inline-flex;
  flex: 0 0 auto !important;
  justify-content: center;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
    0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
}
::v-deep .e-toolbar .e-tbar-btn.e-btn .e-icons {
  color: #fff;
}
::v-deep .e-toolbar .e-overlay[aria-disabled="true"] .e-tbar-btn.e-btn {
  background-color: rgba(0, 0, 0, 0.12) !important;
}
::v-deep .e-toolbar .e-overlay[aria-disabled="true"] .e-icons,
::v-deep .e-toolbar .e-overlay[aria-disabled="true"] .e-tbar-btn-text {
  color: rgba(0, 0, 0, 2.26) !important;
}
::v-deep .e-toolbar .e-toolbar-items .e-toolbar-item .e-tbar-btn-text {
  color: #fff;
}
::v-deep .e-toolbar .e-toolbar-items .e-toolbar-item.e-overlay {
  background: inherit;
}

::v-deep .e-splitter .e-split-bar.e-split-bar-horizontal {
  background: var(--v-pageHeader-lighten2);
  z-index: 1 !important;
  width: 1px !important;
  margin-left: 1px;
  margin-right: 1px;
}
.splitterContent
  /deep/
  .e-split-bar.e-split-bar-horizontal.e-resizable-split-bar::after {
  width: 4px !important;
}
/* split bar color in hover and active state */
::v-deep .e-splitter .e-split-bar.e-split-bar-horizontal.e-split-bar-hover,
.e-splitter .e-split-bar.e-split-bar-horizontal.e-split-bar-active {
  background: var(--v-pageHeader-base);
}

/* default split bar resize handle color */
::v-deep .e-splitter .e-split-bar.e-split-bar-horizontal .e-resize-handler {
  color: var(--v-pageHeader-base);
}

/* default split bar resize handle color in hover and active state */
::v-deep
  .e-splitter
  .e-split-bar.e-split-bar-horizontal.e-split-bar-hover
  .e-resize-handler,
.e-splitter
  .e-split-bar.e-split-bar-horizontal.e-split-bar-active
  .e-resize-handler {
  color: var(--v-pageHeader-base);
}

/* split bar arrows - circular border */
::v-deep
  .e-splitter
  .e-split-bar.e-split-bar-horizontal.e-split-bar-hover
  .e-navigate-arrow.e-arrow-left {
  background-color: var(--v-pageHeader-lighten2) !important;
  opacity: initial;
}

::v-deep
  .e-splitter
  .e-split-bar.e-split-bar-horizontal.e-split-bar-hover
  .e-navigate-arrow.e-arrow-right {
  background-color: var(--v-pageHeader-lighten2) !important;
  opacity: initial;
}
/* split bar arrows */

::v-deep
  .e-splitter
  .e-split-bar.e-split-bar-horizontal
  .e-navigate-arrow.e-arrow-right {
  background-color: var(--v-pageHeader-lighten3) !important;
  opacity: 0.2;
}
.config-title {
  display: flex;
  padding: 14px 16px;
  gap: 8px;
  font-size: 18px;
  font-weight: 500;
  color: #000;
  background: #f1f1f1;
  border-left: 4px solid #006cad;
}
.config-title span {
  margin-top: 4px;
}
.config-icon {
  color: #000;
  font-size: 20px !important;
}
.config-sub-item-title{
  padding-left: 30px;
}
.v-card__text {
  padding: 0px !important;
}
</style>